import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, MenuItem, Box, CircularProgress, Typography } from '@mui/material';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { useApartment } from '../contexts/ApartmentContext';
import { useUnion } from '../contexts/UnionContext';

function ApartmentSwitcher() {
  const { t } = useTranslation();
  const { apartments, activeApartment, loading, switchApartment } = useApartment();
  const { isUnionAdmin, activeRole } = useUnion();

  const roleLabel = {
    ADMIN: t('switcher.admin'),
    ACCOUNTANT: t('switcher.accountant'),
    BOARD_MEMBER: t('switcher.boardMember'),
  }[activeRole] ?? activeRole;

  const handleChange = (event) => {
    const id = event.target.value;
    switchApartment(id ? parseInt(id, 10) : null);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 1 }}>
        <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.5)' }} />
      </Box>
    );
  }

  if (!apartments || apartments.length === 0) return null;

  return (
    <Select
      value={activeApartment?.id ?? ''}
      onChange={handleChange}
      displayEmpty
      size="small"
      variant="outlined"
      renderValue={(id) => {
        const apt = apartments.find(a => a.id === id);
        const label = apt ? `${apt.unit_number} ${t('switcher.apt')}` : (isUnionAdmin ? roleLabel : t('nav.apartment'));
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ApartmentIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.8)' }} />
            <span>{label}</span>
          </Box>
        );
      }}
      sx={{
        color: 'white',
        minWidth: 110,
        maxWidth: 200,
        ml: 1,
        '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.25)' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.5)' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.7)' },
        '.MuiSvgIcon-root': { color: 'rgba(255,255,255,0.7)' },
        fontSize: 14,
      }}
    >
      {isUnionAdmin && (
        <MenuItem value="">
          <Typography variant="body2">{roleLabel}</Typography>
        </MenuItem>
      )}
      {apartments.map((apt) => (
        <MenuItem key={apt.id} value={apt.id}>
          {apt.unit_number} {t('switcher.apt')}{apt.address ? ` — ${apt.address}` : ''}
        </MenuItem>
      ))}
    </Select>
  );
}

export default ApartmentSwitcher;
